import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  FolderOpen, Folder, Plus, Pencil, Trash2, X, ChevronDown,
  ChevronLeft, Package, Save, FolderPlus, ImageOff, Star, ChevronRight, SlidersHorizontal, ImageIcon, Truck
} from 'lucide-react'
import ManageFiltersDialog from './ManageFiltersDialog'
import MediaPickerModal from '@/components/shared/MediaPickerModal'
import { categoriesService } from '@/services/categoriesService'
import { shippingService } from '@/services/shippingService'
import { productsService } from '@/services/productsService'
import RichTextEditor from '@/components/shared/RichTextEditor'
import { usePagination } from '@/hooks/usePagination'
import LoadingSpinner from '@/components/shared/LoadingSpinner'
import EmptyState from '@/components/shared/EmptyState'
import { formatCurrency, getLocalizedField } from '@/utils/formatters'
import { imgUrl } from '@/utils/img'

function DialogOverlay({ children, onClose }) {
  return (
    <div style={{ position:'fixed',inset:0,zIndex:50,display:'flex',alignItems:'center',justifyContent:'center',padding:16,background:'rgba(0,0,0,0.5)',backdropFilter:'blur(4px)' }} onClick={onClose}>
      <div className="animate-in" style={{ background:'#fff',borderRadius:24,padding:28,width:'100%',maxWidth:540,maxHeight:'90vh',overflowY:'auto',boxShadow:'0 32px 64px rgba(0,0,0,0.2)',position:'relative' }} onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} style={{ position:'absolute',top:16,left:16,width:32,height:32,borderRadius:10,background:'#F3F3F3',border:'none',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',color:'#767683',zIndex:10 }}><X size={16}/></button>
        {children}
      </div>
    </div>
  )
}

const EMPTY_FORM = { nameAr:'', nameEn:'', descAr:'', descEn:'', parentId:'', sortOrder:0, image:'', shippingClassId:'' }

export default function CategoriesPage() {
  const { t, i18n } = useTranslation()
  const lang = i18n.language
  const qc   = useQueryClient()

  const [expanded,         setExpanded]         = useState({})
  const [formDialog,       setFormDialog]       = useState({ open:false, mode:'create', category:null, parentId:null })
  const [deleteDialog,     setDeleteDialog]     = useState({ open:false, category:null })
  const [form,             setForm]             = useState(EMPTY_FORM)
  const [selectedCategory, setSelectedCategory] = useState(null)
  const [filtersDialog, setFiltersDialog] = useState({ open: false, category: null })
  const [mediaPickerOpen, setMediaPickerOpen] = useState(false)
  const { page: prodPage, limit: prodLimit, setPage: setProdPage } = usePagination(1, 10)

  const { data: tree, isLoading, isError, refetch } = useQuery({
    queryKey: ['categories-tree'],
    queryFn: categoriesService.getTree,
  })

  const { data: shippingClassesData } = useQuery({
    queryKey: ['shipping-classes'],
    queryFn: shippingService.getClasses,
  })
  const shippingClasses = (shippingClassesData || []).filter((c) => c.isActive)

  const categories = tree || []

  // Products for selected category
  const { data: productsData, isLoading: productsLoading } = useQuery({
    queryKey: ['category-products', selectedCategory?.id, prodPage],
    queryFn: () => productsService.getAll({ categoryId: selectedCategory.id, page: prodPage, limit: prodLimit }),
    enabled: !!selectedCategory,
    retry: 1,
  })
  const catProducts = productsData?.data || []
  const catProdMeta = productsData?.meta || {}

  const invalidate = () => qc.invalidateQueries({ queryKey: ['categories-tree'] })

  const createMut = useMutation({
    mutationFn: (data) => categoriesService.create(data),
    onSuccess: () => { toast.success('تم إنشاء الفئة بنجاح'); invalidate(); closeForm() },
    onError: (e) => toast.error(e?.response?.data?.message || 'فشل إنشاء الفئة'),
  })

  const updateMut = useMutation({
    mutationFn: ({ id, data }) => categoriesService.update(id, data),
    onSuccess: () => { toast.success('تم تحديث الفئة'); invalidate(); closeForm() },
    onError: (e) => toast.error(e?.response?.data?.message || 'فشل التحديث'),
  })

  const deleteMut = useMutation({
    mutationFn: (id) => categoriesService.delete(id),
    onSuccess: () => { toast.success('تم حذف الفئة'); invalidate(); setDeleteDialog({ open:false, category:null }) },
    onError: (e) => toast.error(e?.response?.data?.message || 'فشل الحذف — قد تحتوي على منتجات أو فئات فرعية'),
  })

  const openCreate = (parentId = null) => {
    setForm({ ...EMPTY_FORM, parentId: parentId || '' })
    setFormDialog({ open:true, mode:'create', category:null, parentId })
  }

  const openEdit = (cat, parentId = null) => {
    setForm({
      nameAr:          cat.name?.ar || '',
      nameEn:          cat.name?.en || '',
      descAr:          cat.description?.ar || '',
      descEn:          cat.description?.en || '',
      parentId:        parentId || '',
      sortOrder:       cat.sortOrder || 0,
      image:           cat.image || '',
      shippingClassId: cat.shippingClassId || cat.shippingClass?.id || '',
    })
    setFormDialog({ open:true, mode:'edit', category:cat, parentId })
  }

  const closeForm = () => {
    setFormDialog({ open:false, mode:'create', category:null, parentId:null })
    setForm(EMPTY_FORM)
  }

  const handleSubmit = () => {
    if (!form.nameAr.trim() && !form.nameEn.trim()) {
      toast.error('يرجى إدخال اسم الفئة بالعربي أو الإنجليزي')
      return
    }
    const hasDesc = form.descAr.trim() || form.descEn.trim()
    const payload = {
      name:            { ar: form.nameAr, en: form.nameEn },
      ...(hasDesc && { description: { ar: form.descAr, en: form.descEn } }),
      parentId:        form.parentId || null,
      sortOrder:       Number(form.sortOrder) || 0,
      image:           form.image || undefined,
      shippingClassId: form.shippingClassId || null,
    }
    if (formDialog.mode === 'create') {
      createMut.mutate(payload)
    } else {
      updateMut.mutate({ id: formDialog.category.id, data: payload })
    }
  }

  const toggleExpand = (id) => setExpanded((prev) => ({ ...prev, [id]: !prev[id] }))

  const isPending = createMut.isPending || updateMut.isPending

  // Flatten for parent selector
  const allCats = categories.map((c) => ({ id:c.id, name: c.name?.[lang] || c.name?.ar || c.name?.en || '' }))

  return (
    <div className="space-y-5">
      {/* Header action */}
      <div className="flex items-center justify-between">
        <div>
          {selectedCategory && (
            <div className="flex items-center gap-2 text-sm" style={{ fontFamily:'Tajawal, sans-serif', color:'#767683' }}>
              <span>الفئات</span>
              <ChevronLeft size={14} />
              <span className="font-bold" style={{ color:'#000666' }}>
                {getLocalizedField(selectedCategory.name, lang)}
              </span>
              <button onClick={() => setSelectedCategory(null)}
                className="w-6 h-6 rounded-full flex items-center justify-center"
                style={{ background:'#F3F3F3', color:'#767683' }}>
                <X size={12} />
              </button>
            </div>
          )}
        </div>
        <button onClick={() => openCreate(null)}
          style={{ display:'flex',alignItems:'center',gap:8,padding:'10px 20px',borderRadius:12,fontFamily:'Cairo, sans-serif',fontWeight:700,fontSize:14,color:'#fff',background:'#000666',border:'none',cursor:'pointer',boxShadow:'0 8px 24px rgba(26,35,126,0.2)' }}>
          <Plus size={16} />إضافة فئة رئيسية
        </button>
      </div>

      {/* Main layout: tree + products panel */}
      <div style={{ display:'grid', gridTemplateColumns: selectedCategory ? '1fr 1.4fr' : '1fr', gap:20, alignItems:'start' }}>

        {/* Tree */}
        <div className="hg-card p-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-16"><LoadingSpinner /></div>
          ) : isError ? (
            <div className="flex flex-col items-center gap-4 py-12">
              <p style={{ color:'#BA1A1A',fontWeight:700,fontFamily:'Cairo, sans-serif' }}>فشل تحميل الفئات</p>
              <button onClick={() => refetch()} className="hg-btn-navy px-5 py-2 text-sm">إعادة المحاولة</button>
            </div>
          ) : categories.length === 0 ? (
            <EmptyState icon={FolderOpen} message="لا توجد فئات بعد" action={
              <button onClick={() => openCreate(null)} style={{ display:'flex',alignItems:'center',gap:8,padding:'10px 20px',borderRadius:12,fontFamily:'Cairo, sans-serif',fontWeight:700,fontSize:14,color:'#fff',background:'#000666',border:'none',cursor:'pointer' }}>
                <Plus size={16}/>إضافة أول فئة
              </button>
            }/>
          ) : (
            <div className="space-y-2">
              {categories.map((cat) => (
                <CategoryNode
                  key={cat.id}
                  cat={cat}
                  lang={lang}
                  depth={0}
                  expanded={expanded}
                  selected={selectedCategory?.id}
                  onToggle={toggleExpand}
                  onSelect={(c) => { setSelectedCategory(c); setProdPage(1) }}
                  onEdit={(c) => openEdit(c, null)}
                  onEditChild={(c, parentId) => openEdit(c, parentId)}
                  onAddChild={(parentId) => openCreate(parentId)}
                  onDelete={(c) => setDeleteDialog({ open:true, category:c })}
                />
              ))}
            </div>
          )}
        </div>

        {/* Products panel */}
        {selectedCategory && (
          <div className="hg-card p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-black" style={{ fontFamily:'Cairo, sans-serif', color:'#000666', fontSize:16 }}>
                منتجات: {getLocalizedField(selectedCategory.name, lang)}
              </h3>
              <div className="flex items-center gap-3">
                {catProdMeta.total != null && (
                  <span className="text-xs text-gray-400" style={{ fontFamily:'Tajawal, sans-serif' }}>
                    {catProdMeta.total} منتج
                  </span>
                )}
                <button onClick={() => setFiltersDialog({ open:true, category:selectedCategory })}
                  style={{ display:'flex', alignItems:'center', gap:6, padding:'8px 14px', borderRadius:10, fontFamily:'Cairo, sans-serif', fontWeight:700, fontSize:12, color:'#000666', background:'rgba(0,6,102,0.07)', border:'none', cursor:'pointer', transition:'all 0.15s' }}>
                  <SlidersHorizontal size={14}/>Manage Filters
                </button>
              </div>
            </div>

            {productsLoading ? (
              <div className="flex items-center justify-center py-12"><LoadingSpinner /></div>
            ) : catProducts.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-3"
                  style={{ background:'rgba(0,6,102,0.06)', color:'#000666' }}>
                  <Package size={22} />
                </div>
                <p className="text-sm text-gray-400" style={{ fontFamily:'Tajawal, sans-serif' }}>
                  لا توجد منتجات في هذه الفئة
                </p>
              </div>
            ) : (
              <>
                <div className="space-y-2">
                  {catProducts.map((product) => {
                    const name = getLocalizedField(product.name, lang)
                    const img  = product.images?.find(i => i.isPrimary)?.url || product.images?.[0]?.url
                    return (
                      <div key={product.id}
                        className="flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 transition-colors"
                        style={{ border:'1px solid #F3F3F3' }}>
                        <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0" style={{ background:'#F3F3F3' }}>
                          {img
                            ? <img src={img} alt={name} className="w-full h-full object-cover" />
                            : <div className="w-full h-full flex items-center justify-center"><ImageOff size={14} className="text-gray-300" /></div>
                          }
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-bold text-sm truncate" style={{ fontFamily:'Cairo, sans-serif', color:'#1A1C1C' }}>{name}</p>
                          <p className="text-xs text-gray-400" style={{ fontFamily:'Tajawal, sans-serif' }}>
                            {product.vendor?.storeName ? getLocalizedField(product.vendor.storeName, lang) : '—'}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {product.isFeatured && <Star size={13} style={{ color:'#FF6D00' }} fill="#FF6D00" />}
                          <span className="font-black text-sm" style={{ color:'#FF6D00', fontFamily:'Cairo, sans-serif' }}>
                            {formatCurrency(product.price)}
                          </span>
                        </div>
                      </div>
                    )
                  })}
                </div>

                {/* Pagination */}
                {catProdMeta.totalPages > 1 && (
                  <div className="flex items-center justify-between mt-4 pt-3" style={{ borderTop:'1px solid #F3F3F3' }}>
                    <span className="text-xs text-gray-400" style={{ fontFamily:'Tajawal, sans-serif' }}>
                      صفحة {catProdMeta.page} من {catProdMeta.totalPages}
                    </span>
                    <div className="flex items-center gap-2">
                      <button onClick={() => setProdPage(p => Math.max(1, p-1))} disabled={!catProdMeta.hasPrev}
                        className="w-7 h-7 rounded-lg flex items-center justify-center disabled:opacity-30" style={{ background:'#F3F3F3' }}>
                        <ChevronRight size={14} />
                      </button>
                      <button onClick={() => setProdPage(p => p+1)} disabled={!catProdMeta.hasNext}
                        className="w-7 h-7 rounded-lg flex items-center justify-center disabled:opacity-30" style={{ background:'#F3F3F3' }}>
                        <ChevronLeft size={14} />
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Create / Edit dialog */}
      {formDialog.open && (
        <DialogOverlay onClose={closeForm}>
          <h3 style={{ fontFamily:'Cairo, sans-serif',fontWeight:900,fontSize:18,color:'#1A1C1C',marginBottom:20 }}>
            {formDialog.mode === 'create' ? (formDialog.parentId ? 'إضافة فئة فرعية' : 'إضافة فئة رئيسية') : 'تعديل الفئة'}
          </h3>

          <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:16 }}>
            <div>
              <label style={{ display:'block',fontFamily:'Cairo, sans-serif',fontWeight:700,fontSize:12,color:'#454652',marginBottom:6 }}>الاسم بالعربي *</label>
              <input value={form.nameAr} onChange={(e) => setForm((p) => ({ ...p, nameAr:e.target.value }))}
                placeholder="مثال: إلكترونيات"
                style={{ width:'100%',borderRadius:10,background:'#E2E2E2',border:'none',padding:'10px 14px',fontSize:14,fontFamily:'Tajawal, sans-serif',outline:'none',boxSizing:'border-box',color:'#1A1C1C' }} />
            </div>
            <div>
              <label style={{ display:'block',fontFamily:'Cairo, sans-serif',fontWeight:700,fontSize:12,color:'#454652',marginBottom:6 }}>الاسم بالإنجليزي *</label>
              <input value={form.nameEn} onChange={(e) => setForm((p) => ({ ...p, nameEn:e.target.value }))}
                placeholder="e.g. Electronics" dir="ltr"
                style={{ width:'100%',borderRadius:10,background:'#E2E2E2',border:'none',padding:'10px 14px',fontSize:14,fontFamily:'Tajawal, sans-serif',outline:'none',boxSizing:'border-box',color:'#1A1C1C' }} />
            </div>
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={{ display:'block',fontFamily:'Cairo, sans-serif',fontWeight:700,fontSize:12,color:'#454652',marginBottom:6 }}>الوصف بالعربي</label>
            <RichTextEditor value={form.descAr} onChange={(html) => setForm((p) => ({ ...p, descAr: html }))} placeholder="وصف الفئة بالعربية..." dir="rtl" />
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display:'block',fontFamily:'Cairo, sans-serif',fontWeight:700,fontSize:12,color:'#454652',marginBottom:6 }}>الوصف بالإنجليزي</label>
            <RichTextEditor value={form.descEn} onChange={(html) => setForm((p) => ({ ...p, descEn: html }))} placeholder="Category description in English..." dir="ltr" />
          </div>

          <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:16 }}>
            <div>
              <label style={{ display:'block',fontFamily:'Cairo, sans-serif',fontWeight:700,fontSize:12,color:'#454652',marginBottom:6 }}>الترتيب</label>
              <input type="number" value={form.sortOrder} onChange={(e) => setForm((p) => ({ ...p, sortOrder:e.target.value }))} min={0}
                style={{ width:'100%',borderRadius:10,background:'#E2E2E2',border:'none',padding:'10px 14px',fontSize:14,fontFamily:'Cairo, sans-serif',outline:'none',boxSizing:'border-box',color:'#1A1C1C' }} />
            </div>
            {formDialog.mode === 'create' && !formDialog.parentId && allCats.length > 0 && (
              <div>
                <label style={{ display:'block',fontFamily:'Cairo, sans-serif',fontWeight:700,fontSize:12,color:'#454652',marginBottom:6 }}>الفئة الأب (اختياري)</label>
                <select value={form.parentId} onChange={(e) => setForm((p) => ({ ...p, parentId:e.target.value }))}
                  style={{ width:'100%',borderRadius:10,background:'#E2E2E2',border:'none',padding:'10px 14px',fontSize:14,fontFamily:'Cairo, sans-serif',outline:'none',color:'#1A1C1C',cursor:'pointer' }}>
                  <option value="">بدون أب (رئيسية)</option>
                  {allCats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            )}
          </div>

          <div style={{ marginBottom:16 }}>
            <label style={{ display:'flex',alignItems:'center',gap:6,fontFamily:'Cairo, sans-serif',fontWeight:700,fontSize:12,color:'#454652',marginBottom:6 }}>
              <Truck size={14} style={{ color:'#000666' }} />
              فئة الشحن (Shipping Class)
            </label>
            <select
              value={form.shippingClassId}
              onChange={(e) => setForm((p) => ({ ...p, shippingClassId: e.target.value }))}
              style={{ width:'100%',borderRadius:10,background:'#E2E2E2',border:'none',padding:'10px 14px',fontSize:14,fontFamily:'Cairo, sans-serif',outline:'none',color:'#1A1C1C',cursor:'pointer' }}
            >
              <option value="">
                {form.parentId
                  ? 'تلقائي: توريث فئة الشحن من الفئة الأب (أو شحن عادي إذا لم يكن للأب فئة)'
                  : 'بدون فئة شحن (شحن قياسي عادي للمنتجات بدون تكلفة إضافية)'}
              </option>
              {shippingClasses.map((cls) => {
                const clsName = getLocalizedField(cls.name, lang) || cls.id
                const extra = cls.extraUnitCost && Number(cls.extraUnitCost) > 0 ? ` + ${cls.extraUnitCost} ج.م إضافي` : ''
                return (
                  <option key={cls.id} value={cls.id}>
                    {clsName} ({cls.baseCost} ج.م{extra})
                  </option>
                )
              })}
            </select>
            <p style={{ fontFamily:'Tajawal, sans-serif',fontSize:11,color:'#767683',margin:'6px 0 0',lineHeight:1.5 }}>
              تطبق تكلفة فئة الشحن على منتجات هذه الفئة. الفئات الفرعية ترث فئة شحن الفئة الأب تلقائياً ما لم تحدد لها فئة شحن مستقلة هنا.
            </p>
          </div>

          <div style={{ marginBottom:20 }}>
            <label style={{ display:'block',fontFamily:'Cairo, sans-serif',fontWeight:700,fontSize:12,color:'#454652',marginBottom:6 }}>صورة الفئة (اختياري)</label>
            <div style={{ display:'flex',gap:10,alignItems:'center' }}>
              {form.image && (
                <div style={{ width:48,height:48,borderRadius:8,background:'#F0F0F5',overflow:'hidden',flexShrink:0 }}>
                  <img src={imgUrl(form.image)} alt="preview" style={{ width:'100%',height:'100%',objectFit:'cover' }} />
                </div>
              )}
              <button onClick={() => setMediaPickerOpen(true)}
                style={{ flex:1,display:'flex',alignItems:'center',justifyContent:'center',gap:6,padding:'10px 14px',borderRadius:10,background:'#E2E2E2',color:'#1A1C1C',border:'none',cursor:'pointer',fontFamily:'Tajawal, sans-serif',fontSize:13 }}>
                <ImageIcon size={14} /> {form.image ? 'تغيير الصورة' : 'اختيار صورة'}
              </button>
              {form.image && (
                <button onClick={() => setForm(p => ({ ...p, image:'' }))} title="حذف الصورة"
                  style={{ width:40,height:40,borderRadius:10,background:'rgba(186,26,26,0.06)',color:'#BA1A1A',border:'none',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center' }}>
                  <X size={14} />
                </button>
              )}
            </div>
          </div>

          <div style={{ display:'flex',gap:10 }}>
            <button onClick={handleSubmit} disabled={isPending}
              style={{ flex:1,padding:'11px 0',borderRadius:12,fontFamily:'Cairo, sans-serif',fontWeight:700,fontSize:14,color:'#fff',background:'#000666',border:'none',cursor:isPending?'not-allowed':'pointer',boxShadow:'0 8px 24px rgba(26,35,126,0.15)',opacity:isPending?0.6:1,display:'flex',alignItems:'center',justifyContent:'center',gap:8 }}>
              <Save size={16}/>{isPending ? '...' : (formDialog.mode === 'create' ? 'إنشاء' : 'حفظ التعديلات')}
            </button>
            <button onClick={closeForm}
              style={{ flex:1,padding:'11px 0',borderRadius:12,fontFamily:'Cairo, sans-serif',fontWeight:700,fontSize:14,background:'#F3F3F3',color:'#454652',border:'none',cursor:'pointer' }}>
              إلغاء
            </button>
          </div>
        </DialogOverlay>
      )}

      {/* Manage Filters dialog */}
      {filtersDialog.open && (
        <ManageFiltersDialog
          category={filtersDialog.category}
          onClose={() => setFiltersDialog({ open:false, category:null })}
        />
      )}

      {/* Delete confirm */}
      {deleteDialog.open && (
        <DialogOverlay onClose={() => setDeleteDialog({ open:false, category:null })}>
          <div style={{ textAlign:'center',marginBottom:20 }}>
            <div style={{ width:52,height:52,borderRadius:16,background:'rgba(186,26,26,0.1)',color:'#BA1A1A',display:'flex',alignItems:'center',justifyContent:'center',margin:'0 auto 12px' }}>
              <Trash2 size={24}/>
            </div>
            <h3 style={{ fontFamily:'Cairo, sans-serif',fontWeight:900,fontSize:18,color:'#1A1C1C',marginBottom:8 }}>حذف الفئة</h3>
            <p style={{ fontFamily:'Tajawal, sans-serif',fontSize:14,color:'#767683' }}>
              هل تريد حذف "{deleteDialog.category?.name?.[lang] || deleteDialog.category?.name?.ar}"؟ لا يمكن التراجع عن هذا الإجراء.
            </p>
          </div>
          <div style={{ display:'flex',gap:10 }}>
            <button onClick={() => deleteMut.mutate(deleteDialog.category.id)} disabled={deleteMut.isPending}
              style={{ flex:1,padding:'11px 0',borderRadius:12,fontFamily:'Cairo, sans-serif',fontWeight:700,fontSize:14,color:'#fff',background:'#BA1A1A',border:'none',cursor:'pointer',opacity:deleteMut.isPending?0.6:1 }}>
              {deleteMut.isPending ? '...' : 'حذف'}
            </button>
            <button onClick={() => setDeleteDialog({ open:false, category:null })}
              style={{ flex:1,padding:'11px 0',borderRadius:12,fontFamily:'Cairo, sans-serif',fontWeight:700,fontSize:14,background:'#F3F3F3',color:'#454652',border:'none',cursor:'pointer' }}>
              إلغاء
            </button>
          </div>
        </DialogOverlay>
      )}

      <MediaPickerModal
        open={mediaPickerOpen}
        onClose={() => setMediaPickerOpen(false)}
        folder="categories"
        title="اختر صورة الفئة"
        onSelect={(asset) => setForm((p) => ({ ...p, image: asset.url }))}
      />
    </div>
  )
}

function CategoryNode({ cat, lang, depth, expanded, selected, onToggle, onSelect, onEdit, onEditChild, onAddChild, onDelete, parentShippingClass = null }) {
  const name     = cat.name?.[lang] || cat.name?.ar || cat.name?.en || cat.slug || '—'
  const hasKids  = cat.children?.length > 0
  const isOpen   = expanded[cat.id]
  const isParent = depth === 0
  const isSelected = selected === cat.id
  const effectiveShippingClass = cat.shippingClass || parentShippingClass

  return (
    <div>
      <div
        style={{
          display:'flex', alignItems:'center', gap:10, padding:'12px 16px', borderRadius:14,
          background: isSelected ? 'rgba(0,6,102,0.09)' : isParent ? 'rgba(0,6,102,0.04)' : 'transparent',
          border: isSelected ? '1.5px solid rgba(0,6,102,0.2)' : isParent ? '1px solid rgba(0,6,102,0.08)' : 'none',
          marginRight: depth > 0 ? 28 : 0, transition:'background 0.15s', cursor:'pointer',
        }}
        onClick={() => onSelect(cat)}
        onMouseEnter={(e) => { if(!isSelected) e.currentTarget.style.background = isParent ? 'rgba(0,6,102,0.06)' : '#F9F9F9' }}
        onMouseLeave={(e) => { if(!isSelected) e.currentTarget.style.background = isParent ? 'rgba(0,6,102,0.04)' : 'transparent' }}
      >
        {/* Expand arrow */}
        {hasKids ? (
          <button onClick={(e) => { e.stopPropagation(); onToggle(cat.id) }} style={{ width:28,height:28,borderRadius:8,background:'rgba(0,6,102,0.08)',color:'#000666',border:'none',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,transition:'transform 0.2s' }}>
            <ChevronLeft size={15} style={{ transform: isOpen ? 'rotate(-90deg)' : 'rotate(0deg)', transition:'transform 0.2s' }}/>
          </button>
        ) : (
          <div style={{ width:28,height:28,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0 }}>
            <div style={{ width:6,height:6,borderRadius:'50%',background: isParent ? '#000666' : '#E8E8E8' }}/>
          </div>
        )}

        {/* Icon */}
        {isParent
          ? <FolderOpen size={18} style={{ color:'#000666',flexShrink:0 }}/>
          : <Folder size={16} style={{ color:'#767683',flexShrink:0 }}/>
        }

        {/* Name & Shipping Badge */}
        <div style={{ flex:1,minWidth:0,display:'flex',alignItems:'center',gap:8,flexWrap:'wrap' }}>
          <span style={{ fontFamily:'Cairo, sans-serif',fontWeight: isParent ? 800 : 600,fontSize: isParent ? 15 : 14,color: isParent ? '#000666' : '#454652' }}>
            {name}
          </span>
          {cat.children?.length > 0 && (
            <span style={{ fontSize:11,color:'#767683',fontFamily:'Tajawal, sans-serif' }}>({cat.children.length} فرعية)</span>
          )}
          {cat._count?.products != null && (
            <span style={{ fontSize:11,color:'#767683',fontFamily:'Tajawal, sans-serif' }}>· {cat._count.products} منتج</span>
          )}

          {cat.shippingClass ? (
            <span style={{ display:'inline-flex',alignItems:'center',gap:4,fontSize:11,fontFamily:'Tajawal, sans-serif',fontWeight:700,padding:'2px 8px',borderRadius:6,background:'rgba(0,6,102,0.08)',color:'#000666' }} title="فئة شحن محددة">
              <Truck size={12}/>{getLocalizedField(cat.shippingClass.name, lang)} ({cat.shippingClass.baseCost} ج.م)
            </span>
          ) : parentShippingClass ? (
            <span style={{ display:'inline-flex',alignItems:'center',gap:4,fontSize:11,fontFamily:'Tajawal, sans-serif',padding:'2px 8px',borderRadius:6,background:'#F3F4F6',color:'#6B7280' }} title="موروث من الفئة الأب">
              <Truck size={12}/>موروث: {getLocalizedField(parentShippingClass.name, lang)} ({parentShippingClass.baseCost} ج.م)
            </span>
          ) : null}
        </div>

        {/* Actions */}
        <div style={{ display:'flex',gap:6,flexShrink:0 }} onClick={e => e.stopPropagation()}>
          {isParent && (
            <button onClick={() => onAddChild(cat.id)} title="إضافة فئة فرعية"
              style={{ display:'flex',alignItems:'center',gap:5,padding:'5px 10px',borderRadius:8,fontSize:12,fontWeight:700,fontFamily:'Cairo, sans-serif',background:'rgba(27,109,36,0.08)',color:'#1B6D24',border:'none',cursor:'pointer',transition:'all 0.15s' }}
              onMouseEnter={(e) => e.currentTarget.style.background='rgba(27,109,36,0.16)'}
              onMouseLeave={(e) => e.currentTarget.style.background='rgba(27,109,36,0.08)'}
            >
              <Plus size={12}/>فرعية
            </button>
          )}
          <button onClick={() => depth === 0 ? onEdit(cat) : onEditChild(cat, cat.parentId)} title="تعديل"
            style={{ width:30,height:30,borderRadius:8,background:'rgba(0,6,102,0.06)',color:'#000666',border:'none',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',transition:'all 0.15s' }}
            onMouseEnter={(e) => { e.currentTarget.style.background='rgba(0,6,102,0.15)'; e.currentTarget.style.transform='scale(1.08)' }}
            onMouseLeave={(e) => { e.currentTarget.style.background='rgba(0,6,102,0.06)'; e.currentTarget.style.transform='' }}
          >
            <Pencil size={13}/>
          </button>
          <button onClick={() => onDelete(cat)} title="حذف"
            style={{ width:30,height:30,borderRadius:8,background:'rgba(186,26,26,0.06)',color:'#BA1A1A',border:'none',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',transition:'all 0.15s' }}
            onMouseEnter={(e) => { e.currentTarget.style.background='rgba(186,26,26,0.15)'; e.currentTarget.style.transform='scale(1.08)' }}
            onMouseLeave={(e) => { e.currentTarget.style.background='rgba(186,26,26,0.06)'; e.currentTarget.style.transform='' }}
          >
            <Trash2 size={13}/>
          </button>
        </div>
      </div>

      {/* Children */}
      {hasKids && isOpen && (
        <div style={{ marginTop:4,marginBottom:4 }}>
          {cat.children.map((child) => (
            <CategoryNode key={child.id} cat={child} lang={lang} depth={depth+1}
              expanded={expanded} selected={selected} onToggle={onToggle} onSelect={onSelect}
              onEdit={onEditChild} onEditChild={onEditChild}
              onAddChild={onAddChild} onDelete={onDelete}
              parentShippingClass={effectiveShippingClass}
            />
          ))}
        </div>
      )}
    </div>
  )
}
